// ./src/components/features/Memberships/MembershipImportDrawer/MembershipImportDrawer.tsx
//
// ROADMAP #271 — a member list from a spreadsheet. The host picks a .csv, sees which of its columns become which field
// (guessed from the headers, changeable), chooses a type for rows that name none and how slashed dates read, and hands
// the WHOLE list to the server, which imports it in the background (`MembershipService.startImport` → a bulk job). The
// page follows the job (`MembershipImportStatus`); the drawer closes as soon as the list is stored.
//
// ⚠ The server owns every rule (owner, the FE checks presence only): a row's bad date, unknown type or missing number is
//   refused by the server ROW BY ROW and listed in the report — the drawer never re-validates a cell.
// ⚠ Owner (2026-10-30): a number already in the account is UPDATED with what the row says; a blank cell changes nothing.
import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Stack, FileInput, Text, Table, Alert, Box, Group, Badge, Button, Paper } from "@mantine/core";
import { OpResult } from "@sdflc/api-helpers";
import { useTranslation } from "react-i18next";

import { useFormSnapshot, useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";
import { AppDrawer, AppIcon, SelectField } from "@/components/generic";
import { useOptionalMembershipTypesContext } from "@/contexts/MembershipTypesContext/MembershipTypesContext";
import { BulkJob } from "@/utils/api/BulkJobService";
import {
  MEMBERSHIP_IMPORT_DATE_ORDER,
  MembershipImportDateOrder,
  MembershipImportField,
  MembershipImportRow,
  MembershipService,
} from "@/utils/api/MembershipService";
import { buildCsv, downloadCsv, formatErrors, getFormErrors, DRAWER_COMBOBOX_PROPS } from "@/utils";

// ============================================
// Types
// ============================================

export interface MembershipImportDrawerProps {
  opened: boolean;
  onClose: () => void;
  /** The list is stored and the server has started on it — the page follows the job. */
  onStarted?: (job: BulkJob) => void;
}

interface ParsedCsv {
  headers: string[];
  rows: string[][];
}

const PREVIEW_ROW_LIMIT = 8;

/** The server's input names — each one is shown at its own control, never only in the footer. */
const KNOWN_FORM_FIELDS = ["membershipTypeId", "dateOrder", "rows"];

/** "Not imported" in a column's picker. */
const SKIP_COLUMN = "";

/** The fields a column can become, in the order the picker lists them, with the host's words for each. */
const FIELD_LABELS: Array<{ field: MembershipImportField; key: string; text: string }> = [
  { field: "membershipNumber", key: "memberships:import.field.membershipNumber", text: "Membership number" },
  { field: "membershipTypeName", key: "memberships:import.field.membershipTypeName", text: "Membership type" },
  { field: "startsOn", key: "memberships:import.field.startsOn", text: "Starts on" },
  { field: "expiresOn", key: "memberships:import.field.expiresOn", text: "Expires on" },
  { field: "company", key: "memberships:import.field.company", text: "Business / company" },
  { field: "contactName", key: "memberships:import.field.contactName", text: "Contact's full name" },
  { field: "firstName", key: "memberships:import.field.firstName", text: "Contact's first name" },
  { field: "lastName", key: "memberships:import.field.lastName", text: "Contact's last name" },
  { field: "email", key: "memberships:import.field.email", text: "Email" },
  { field: "phone", key: "memberships:import.field.phone", text: "Phone" },
  { field: "jobTitle", key: "memberships:import.field.jobTitle", text: "Job title" },
  { field: "notes", key: "memberships:import.field.notes", text: "Notes" },
];

/**
 * Header words each field is guessed from, compared after `headerKey` (case, accents, spaces and punctuation removed).
 * English first, then the app's other languages (French, Spanish, Russian) — a list exported in any of them maps itself.
 */
const HEADER_ALIASES: Record<MembershipImportField, string[]> = {
  membershipNumber: [
    "membershipnumber", "membershipno", "membershipid", "membernumber", "memberno", "memberid", "number", "no", "cardnumber", "card", "id",
    "numero", "numeromembre", "numerodemembre", "numerodesocio", "numerosocio", "номер", "номерчленства", "номеркарты",
  ],
  membershipTypeName: [
    "membershiptype", "membershiplevel", "type", "level", "tier", "plan", "category",
    "typedadhesion", "niveau", "categorie", "tipo", "nivel", "categoria", "тип", "уровень", "категория",
  ],
  startsOn: [
    "startson", "startdate", "start", "starts", "joined", "joindate", "membersince", "since", "validfrom", "from",
    "debut", "datededebut", "adhesion", "inicio", "fechadeinicio", "alta", "начало", "датаначала", "датавступления",
  ],
  expiresOn: [
    "expireson", "expirydate", "expiry", "expires", "expiration", "expirationdate", "renewaldate", "renewal", "validuntil", "until", "enddate", "end",
    "expire", "datedexpiration", "fin", "renouvellement", "vence", "vencimiento", "fechadevencimiento", "renovacion", "окончание", "датаокончания", "действуетдо",
  ],
  company: [
    "company", "companyname", "business", "businessname", "organization", "organisation", "member", "membername", "account", "accountname",
    "societe", "entreprise", "organisme", "empresa", "negocio", "organizacion", "компания", "организация", "фирма",
  ],
  contactName: ["contactname", "contact", "fullname", "name", "primarycontact", "nomcomplet", "nombrecompleto", "contacto", "контакт", "фио"],
  firstName: ["firstname", "first", "givenname", "prenom", "nombre", "имя"],
  lastName: ["lastname", "last", "surname", "familyname", "nom", "nomdefamille", "apellido", "apellidos", "фамилия"],
  email: ["email", "emailaddress", "mail", "courriel", "correo", "correoelectronico", "почта", "электроннаяпочта"],
  phone: ["phone", "phonenumber", "mobile", "cell", "telephone", "tel", "portable", "telefono", "movil", "celular", "телефон", "мобильный"],
  jobTitle: ["jobtitle", "title", "position", "role", "poste", "fonction", "cargo", "puesto", "должность"],
  notes: ["notes", "note", "comments", "comment", "remarques", "commentaires", "notas", "comentarios", "примечания", "заметки", "комментарий"],
};

// A "#" in a header means number ("Member #" is the membership number, not the member's name). Accents are dropped
// ("Société" = "societe"); letters of any alphabet are kept, so a Russian header is compared as written.
const headerKey = (h: string): string =>
  h
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/#/g, "number")
    .replace(/[^\p{L}\p{N}]/gu, "");

// ⚠ The aliases go through the SAME key as the headers — "мобильный" loses its breve exactly as a header does.
const ALIAS_KEYS: Record<string, Set<string>> = Object.fromEntries(
  Object.entries(HEADER_ALIASES).map(([field, words]) => [field, new Set(words.map(headerKey))])
);

/** The field each column starts as: the first unclaimed field whose aliases name it; none when nothing does. */
const guessMapping = (headers: string[]): string[] => {
  const taken = new Set<string>();

  return headers.map((h) => {
    const key = headerKey(h);
    const hit = FIELD_LABELS.find(({ field }) => !taken.has(field) && ALIAS_KEYS[field].has(key));

    if (!hit) {
      return SKIP_COLUMN;
    }

    taken.add(hit.field);

    return hit.field;
  });
};

const SLASHED_DATE = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/;

/** Which order the file's slashed dates are in, when one of them gives it away (a 25 can only be a day). */
const guessDateOrder = (values: string[]): MembershipImportDateOrder | null => {
  for (const v of values) {
    const m = SLASHED_DATE.exec(v.trim());

    if (m && Number(m[1]) > 12) {
      return MEMBERSHIP_IMPORT_DATE_ORDER.DMY;
    }

    if (m && Number(m[2]) > 12) {
      return MEMBERSHIP_IMPORT_DATE_ORDER.MDY;
    }
  }

  return null;
};

// ============================================
// CSV parser (quoted fields, escaped quotes, embedded newlines; comma or semicolon; a leading BOM dropped)
// ============================================

const parseCsv = (raw: string): ParsedCsv => {
  const text = raw.replace(/^\uFEFF/, "");
  const firstLine = text.split(/\r?\n/, 1)[0] ?? "";
  // A spreadsheet saved where the decimal mark is a comma writes semicolons between columns.
  const delimiter = (firstLine.match(/;/g)?.length ?? 0) > (firstLine.match(/,/g)?.length ?? 0) ? ";" : ",";
  const allRows: string[][] = [];
  let field = "";
  let row: string[] = [];
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }

      continue;
    }

    if (char === '"') {
      inQuotes = true;
    } else if (char === delimiter) {
      row.push(field);
      field = "";
    } else if (char === "\n") {
      row.push(field);
      allRows.push(row);
      row = [];
      field = "";
    } else if (char !== "\r") {
      field += char;
    }
  }

  if (field.length > 0 || row.length > 0) {
    row.push(field);
    allRows.push(row);
  }

  const nonEmpty = allRows.filter((r) => r.some((cell) => cell.trim() !== ""));
  const headers = nonEmpty.length > 0 ? nonEmpty[0].map((h) => h.trim()) : [];

  return { headers, rows: nonEmpty.slice(1) };
};

// ============================================
// Component
// ============================================

export const MembershipImportDrawer: React.FC<MembershipImportDrawerProps> = ({ opened, onClose, onStarted }) => {
  const { t } = useTranslation(["memberships", "common"]);
  const typesCtx = useOptionalMembershipTypesContext();

  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [mapping, setMapping] = useState<string[]>([]);
  const [membershipTypeId, setMembershipTypeId] = useState<string | null>(null);
  const [dateOrder, setDateOrder] = useState<MembershipImportDateOrder>(MEMBERSHIP_IMPORT_DATE_ORDER.MDY);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [lastError, setLastError] = useState<OpResult | null>(null);
  // Presence only: a list with no type column needs a type chosen for its rows.
  const [typeMissing, setTypeMissing] = useState(false);
  // Each open and each chosen file is its own read: a parse still answering from an earlier one (left by Back mid-read)
  // must not land on this one and enable Import with no file shown.
  const readIdRef = useRef(0);

  // Esc / a click outside / Cancel ask before discarding a chosen file (#123).
  const { isDirty, seedFrom, clear } = useFormSnapshot({ fileName: file?.name ?? null, membershipTypeId, mapping });

  // A URL stage (ROADMAP #120): it can also close by Back, past `handleClose` — so it starts empty on every open.
  useEffect(() => {
    if (opened) {
      readIdRef.current += 1;
      setFile(null);
      setParsed(null);
      setMapping([]);
      setMembershipTypeId(null);
      setDateOrder(MEMBERSHIP_IMPORT_DATE_ORDER.MDY);
      setParseError(null);
      setIsParsing(false);
      setLastError(null);
      setTypeMissing(false);
      seedFrom({ fileName: null, membershipTypeId: null, mapping: [] });
    } else {
      clear();
    }
  }, [opened, seedFrom, clear]);

  const typeOptions = useMemo(
    () => (typesCtx?.membershipTypes ?? []).map((type) => ({ value: type.id!, label: type.name ?? type.id! })),
    [typesCtx?.membershipTypes]
  );

  const fieldOptions = useMemo(
    () => [
      { value: SKIP_COLUMN, label: t("memberships:import.field.skip", { defaultValue: "Not imported" }) },
      ...FIELD_LABELS.map(({ field, key, text }) => ({ value: field, label: t(key, { defaultValue: text }) })),
    ],
    [t]
  );

  const handleFileChange = useCallback((next: File | null) => {
    setFile(next);
    setParsed(null);
    setMapping([]);
    setParseError(null);
    setLastError(null);

    if (!next) {
      return;
    }

    setIsParsing(true);

    const readId = ++readIdRef.current;
    const reader = new FileReader();

    reader.onload = () => {
      if (readId !== readIdRef.current) {
        return;
      }

      try {
        const result = parseCsv(String(reader.result ?? ""));

        if (result.headers.length === 0 || result.rows.length === 0) {
          setParseError(t("memberships:import.errors.empty", { defaultValue: "That file has no data rows." }));
          setParsed(null);
        } else {
          const guessed = guessMapping(result.headers);
          setParsed(result);
          setMapping(guessed);

          const dateCols = guessed.flatMap((f, i) => (f === "startsOn" || f === "expiresOn" ? [i] : []));
          const order = guessDateOrder(result.rows.flatMap((r) => dateCols.map((i) => r[i] ?? "")));

          if (order) {
            setDateOrder(order);
          }
        }
      } catch {
        setParseError(t("memberships:import.errors.parse", { defaultValue: "Couldn't read that file as CSV." }));
        setParsed(null);
      } finally {
        setIsParsing(false);
      }
    };

    reader.onerror = () => {
      if (readId !== readIdRef.current) {
        return;
      }

      setParseError(t("memberships:import.errors.read", { defaultValue: "Couldn't read that file." }));
      setIsParsing(false);
    };

    reader.readAsText(next);
  }, [t]);

  /** A column takes a field; a column that had it already gives it up (two columns cannot both be the email). */
  const setColumnField = useCallback((index: number, field: string | null) => {
    setMapping((prev) =>
      prev.map((f, i) => {
        if (i === index) {
          return field ?? SKIP_COLUMN;
        }

        return field && f === field ? SKIP_COLUMN : f;
      })
    );
  }, []);

  const hasTypeColumn = mapping.includes("membershipTypeName");
  const mappedCount = mapping.filter(Boolean).length;

  // A slashed date in a date column is what makes the order matter; the example shown is the file's own.
  const slashedExample = useMemo(() => {
    if (!parsed) {
      return null;
    }

    const dateCols = mapping.flatMap((f, i) => (f === "startsOn" || f === "expiresOn" ? [i] : []));

    for (const r of parsed.rows) {
      for (const i of dateCols) {
        const v = (r[i] ?? "").trim();

        if (SLASHED_DATE.test(v)) {
          return v;
        }
      }
    }

    return null;
  }, [parsed, mapping]);

  const { fields: fieldErrors, genericErrors } = useMemo(() => getFormErrors(lastError, KNOWN_FORM_FIELDS), [lastError]);

  const previewRows = useMemo(() => (parsed ? parsed.rows.slice(0, PREVIEW_ROW_LIMIT) : []), [parsed]);

  const handleImport = useCallback(async () => {
    if (!parsed || isStarting) {
      return;
    }

    if (!hasTypeColumn && !membershipTypeId) {
      setTypeMissing(true);

      return;
    }

    const rows: MembershipImportRow[] = parsed.rows.map((cells) => {
      const row: MembershipImportRow = {};

      mapping.forEach((field, i) => {
        const value = (cells[i] ?? "").trim();

        if (field && value) {
          row[field as MembershipImportField] = value;
        }
      });

      return row;
    });

    setIsStarting(true);
    setLastError(null);
    const result = await MembershipService.startImport({ membershipTypeId, dateOrder, rows });
    setIsStarting(false);

    if (!result.didSucceed()) {
      setLastError(result);

      return;
    }

    const job = (result.getDataFirst?.() ?? (result as any).data?.[0]) as BulkJob | undefined;

    if (job?.id) {
      onStarted?.(job);
    }

    onClose();
  }, [parsed, isStarting, hasTypeColumn, membershipTypeId, mapping, dateOrder, onStarted, onClose]);

  /** A sample with every column the import reads, so a host can fill it in or copy its headers. */
  const downloadSample = useCallback(() => {
    downloadCsv(
      "memberships-sample.csv",
      buildCsv(
        ["Membership number", "Membership type", "Starts on", "Expires on", "Company", "First name", "Last name", "Email", "Phone", "Job title", "Notes"],
        [["M-1001", typeOptions[0]?.label ?? "Gold", "2026-01-01", "2026-12-31", "Acme Bakery", "Jane", "Doe", "jane@example.com", "403 555 0199", "Owner", ""]]
      )
    );
  }, [typeOptions]);

  const canImport = !!parsed && !isParsing && !isStarting && mappedCount > 0;

  // ⌘S / ⌘Enter only. Esc is AppDrawer's own — it asks before discarding a chosen file (#121, #123).
  useModalShortcuts({ onConfirm: handleImport, canConfirm: canImport, enabled: opened });

  const typeError =
    fieldErrors.membershipTypeId ||
    (typeMissing && !hasTypeColumn && !membershipTypeId
      ? t("memberships:import.typeRequired", {
          defaultValue: "Choose a type for these memberships, or set one of the columns to Membership type.",
        })
      : null);

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={t("memberships:import.title", { defaultValue: "Import memberships" })}
      position="right"
      size="lg"
      closable={!isStarting}
      loading={isStarting}
      errors={formatErrors(genericErrors)}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="confirm-cancel"
      onConfirm={handleImport}
      confirmLabel={t("memberships:import.confirm", { defaultValue: "Import" })}
      confirmIcon="Upload"
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={!canImport}
      isDirty={isDirty}
    >
      <Stack gap="md">
        <Text size="md" c="dimmed">
          {t("memberships:import.description", {
            defaultValue:
              "Upload your member list as a CSV. Each row becomes a membership, and the business and contact it names are added to your contacts — or matched to ones you already have.",
          })}
        </Text>

        <Alert variant="light" color="blue" icon={<AppIcon iconName="InfoCircle" size={16} />}>
          {t("memberships:import.updateNote", {
            defaultValue:
              "A membership number you already have is updated with what the row says. Empty cells change nothing, so you can re-import a list to refresh expiry dates.",
          })}
        </Alert>

        <Group gap="sm" align="flex-end" wrap="nowrap">
          <FileInput
            label={t("memberships:import.fields.file.label", { defaultValue: "CSV file" })}
            placeholder={t("memberships:import.fields.file.placeholder", { defaultValue: "Choose a .csv file" })}
            accept=".csv,text/csv"
            value={file}
            onChange={handleFileChange}
            clearable
            leftSection={<AppIcon iconName="FileText" size={16} />}
            size="md"
            error={fieldErrors.rows}
            disabled={isStarting}
            style={{ flex: 1, minWidth: 0 }}
          />
          <Button variant="subtle" size="md" onClick={downloadSample} leftSection={<AppIcon iconName="Download" size={16} />}>
            {t("memberships:import.sample", { defaultValue: "Sample" })}
          </Button>
        </Group>

        {parseError && (
          <Alert variant="light" color="red" icon={<AppIcon iconName="AlertTriangle" size={16} />}>
            {parseError}
          </Alert>
        )}

        {parsed && (
          <>
            <SelectField
              label={t("memberships:import.fields.type.label", { defaultValue: "Membership type" })}
              description={
                hasTypeColumn
                  ? t("memberships:import.fields.type.withColumn", {
                      defaultValue: "For rows whose type cell is empty. A type the file names must already exist on the Membership types tab.",
                    })
                  : t("memberships:import.fields.type.noColumn", { defaultValue: "Every new membership in this file gets this type." })
              }
              placeholder={t("memberships:import.fields.type.placeholder", { defaultValue: "Select a type" })}
              data={typeOptions}
              value={membershipTypeId}
              onChange={(_name, v) => {
                setMembershipTypeId(v);
                setTypeMissing(false);
              }}
              error={typeError}
              disabled={isStarting}
              searchable
              clearable
              comboboxProps={DRAWER_COMBOBOX_PROPS}
              size="md"
            />

            {slashedExample || fieldErrors.dateOrder ? (
              <SelectField
                label={t("memberships:import.fields.dateOrder.label", {
                  defaultValue: "In this file, {{example}} means",
                  example: slashedExample ?? "03/04/2026",
                })}
                data={[
                  {
                    value: MEMBERSHIP_IMPORT_DATE_ORDER.MDY,
                    label: t("memberships:import.fields.dateOrder.mdy", { defaultValue: "Month first (03/04 is March 4)" }),
                  },
                  {
                    value: MEMBERSHIP_IMPORT_DATE_ORDER.DMY,
                    label: t("memberships:import.fields.dateOrder.dmy", { defaultValue: "Day first (03/04 is 3 April)" }),
                  },
                ]}
                value={dateOrder}
                onChange={(_name, v) => setDateOrder((v as MembershipImportDateOrder) ?? MEMBERSHIP_IMPORT_DATE_ORDER.MDY)}
                error={fieldErrors.dateOrder}
                disabled={isStarting}
                comboboxProps={DRAWER_COMBOBOX_PROPS}
                size="md"
              />
            ) : null}

            <Paper withBorder radius="md" p="md">
              <Stack gap="sm">
                <Group gap="xs" justify="space-between">
                  <Text size="md" fw={600}>
                    {t("memberships:import.columnsTitle", { defaultValue: "What each column becomes" })}
                  </Text>
                  <Badge variant="light" color={mappedCount > 0 ? "green" : "yellow"}>
                    {t("memberships:import.columnsMapped", {
                      defaultValue: "{{mapped}} of {{count}} columns imported",
                      mapped: mappedCount,
                      count: parsed.headers.length,
                    })}
                  </Badge>
                </Group>
                {mappedCount === 0 ? (
                  <Text size="md" c="orange.8">
                    {t("memberships:import.noColumns", {
                      defaultValue: "None of the column names were recognised. Choose what each column becomes below.",
                    })}
                  </Text>
                ) : null}
                {parsed.headers.map((header, i) => (
                  <SelectField
                    key={`col-${i}`}
                    label={header || t("memberships:import.column", { defaultValue: "Column {{n}}", n: i + 1 })}
                    description={
                      parsed.rows[0]?.[i]?.trim()
                        ? t("memberships:import.example", { defaultValue: "e.g. {{value}}", value: parsed.rows[0][i].trim() })
                        : undefined
                    }
                    data={fieldOptions}
                    value={mapping[i] ?? SKIP_COLUMN}
                    onChange={(_name, v) => setColumnField(i, v)}
                    disabled={isStarting}
                    comboboxProps={DRAWER_COMBOBOX_PROPS}
                    size="md"
                  />
                ))}
              </Stack>
            </Paper>

            <Stack gap="xs">
              <Group gap="xs">
                <Badge variant="light" color="green">
                  {t("memberships:import.rowCount", { defaultValue: "Rows: {{count}}", count: parsed.rows.length })}
                </Badge>
                <Text size="md" c="dimmed">
                  {t("memberships:import.previewNote", { defaultValue: "Showing the first {{count}}.", count: previewRows.length })}
                </Text>
              </Group>

              <Box style={{ overflowX: "auto" }}>
                <Table striped highlightOnHover withTableBorder withColumnBorders>
                  <Table.Thead>
                    <Table.Tr>
                      {parsed.headers.map((header, i) => (
                        <Table.Th key={`h-${i}`} style={{ whiteSpace: "nowrap" }}>
                          {header || `#${i + 1}`}
                        </Table.Th>
                      ))}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {previewRows.map((row, ri) => (
                      <Table.Tr key={`r-${ri}`}>
                        {parsed.headers.map((_h, ci) => (
                          <Table.Td key={`c-${ri}-${ci}`} style={{ whiteSpace: "nowrap", opacity: mapping[ci] ? 1 : 0.45 }}>
                            {row[ci] ?? ""}
                          </Table.Td>
                        ))}
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Box>
            </Stack>
          </>
        )}
      </Stack>
    </AppDrawer>
  );
};
